import * as React from 'react';
import {
  RouterProvider as ReactAriaRouterProvider,
  RouterConfig,
} from 'react-aria-components';

type DesignSystemProviderProps = {
  /** Children nodes to render. */
  children: React.ReactNode;
  /**
   * Custom link component to be used wherever an `@zapier/design-system` `<Link>` is used in the
   * Design System. Can be used to hook up `<Link>`'s to a custom routing
   * solution. Defaults to an `<a>` tag if none is provided.
   **/
  LinkComponent: any;
  /**
   * A function to navigate to a new path wherever a `@zapier/design-system-beta` `<Link>` is used in the Design System. This is used to enable client-side routing for the `@zapier/design-system-beta` `<Link>` component.
   */
  navigate?: (path: string, routerOptions?: RouterConfig) => void;
};

const defaultContext = {
  LinkComponent: 'a' as any,
};

const DesignSystemContext = React.createContext(defaultContext);

export const useDesignSystem = () => React.useContext(DesignSystemContext);

/**
 * Allows consumers to configure various details of the Design System. This
 * should be used at the root of your application.
 *
 * To enable client-side routing for the **legacy `@zapier/design-system` `Link` component**, you must pass a `LinkComponent` that supports client-side routing.
 *
 * @example
 * ```tsx
 * import { Link } from 'next/link';
 * import { DesignSystemProvider } from '@zapier/design-system';
 *
 * export function ClientProviders({ children }: { children: React.ReactNode }) {
 *   return (
 *     <DesignSystemProvider LinkComponent={Link}>
 *       {children}
 *     </DesignSystemProvider>
 *   );
 * }
 * ```
 *
 * To enable client-side routing for the **`@zapier/design-system-beta` `Link` component**, you must pass a `navigate` function from a framework or client side router.
 *
 * @example
 * ```tsx
 * 'use client';
 * import { useRouter } from 'next/navigation';
 * import { DesignSystemProvider } from '@zapier/design-system';
 *
 * export function ClientProviders({ children }: { children: React.ReactNode }) {
 *   const router = useRouter();
 *
 *   return (
 *     <DesignSystemProvider navigate={(path: string) => router.push(path)}>
 *       {children}
 *     </DesignSystemProvider>
 *   );
 * }
 * ```
 * Ensure that your app only installs one version of `@zapier/design-system`, `@zapier/design-system-context`, and `react-aria-components`. Multiple versions of these packages in the same app will break client-side routing.
 */
export const DesignSystemProvider = ({
  children,
  LinkComponent,
  navigate,
}: DesignSystemProviderProps) => {
  const content = (
    <DesignSystemContext.Provider
      value={{
        LinkComponent,
      }}
    >
      {React.Children.only(children)}
    </DesignSystemContext.Provider>
  );

  return children ? (
    // ReactAriaRouterProvider requires a navigate function, so we only render it if `navigate` is provided
    navigate ? (
      <ReactAriaRouterProvider navigate={navigate}>
        {content}
      </ReactAriaRouterProvider>
    ) : (
      content
    )
  ) : null;
};
